<script setup>
import { i18n } from 'boot/i18n';
import Banner from 'components/UI/Banner.vue';
import { useDialogPluginComponent } from 'quasar';
import {
  notifyNegative,
  notifySuccess,
  notifyWarning,
} from 'src/composables/interactions';
import { clients as useClientsStore } from 'src/store/clients';
import { computed } from 'vue';

import { useCareModelReviewAssignments } from '../composables/useCareModelReviewAssignments';
import { useCohortsFiltersColumns } from '../composables/useCohortsFiltersColumns';
import { useReviewAssignCohortsDialog } from '../composables/useReviewAssignCohortsDialog';
import ReviewManualAssignmentsTable from './ReviewManualAssignmentsTable.vue';
import ReviewSuggestedAssignmentsTable from './ReviewSuggestedAssignmentsTable.vue';

defineOptions({ name: 'ReviewAssignCohortsDialog' });

const props = defineProps({
  cohorts: {
    type: Array,
    required: true,
  },

  surface: {
    type: String,
    required: true,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const {
  dialogRef,
  onDialogHide,
  onDialogOK,
  onDialogCancel,
} = useDialogPluginComponent();

const clientsStore = useClientsStore();
const { columns } = useCohortsFiltersColumns();

const reviewAssignments = useCareModelReviewAssignments();

const {
  isProcessing,
  manualAssignments,
  assignClients,
} = reviewAssignments;

const dialogState = useReviewAssignCohortsDialog({
  cohorts: computed(() => props.cohorts),
  reviewAssignments,
  surface: props.surface,
});

const {
  STEPS,
  step,
  suggestedReviewRows,
  cohortOptions,
  hasSuggestedAssignments,
  hasManualAssignments,
  suggestedStepButtonLabel,
  canGoBack,
  canGoNext,
  canAssign,
  initializeReview,
  goToNextStep,
  goToPreviousStep,
  trackStepTwoCompleted,
  trackDialogCancelled,
  toggleAllSuggested,
  toggleSuggestedRow,
  updateManualSelection,
  collectAssignments,
} = dialogState;

function onToggleAll(value) {
  toggleAllSuggested(value);
}

function onToggleSuggestedRow(clientId, value) {
  toggleSuggestedRow(clientId, value);
}

async function onAssign() {
  const assignments = collectAssignments();

  if (!assignments.length) {
    notifyWarning(
      i18n.t('settings.careModel.cohorts.review.noAssignmentsSelected'),
    );
    return;
  }

  if (step.value === STEPS.MANUAL) {
    trackStepTwoCompleted();
  }

  try {
    await assignClients({ assignments });

    notifySuccess(
      i18n.t('settings.careModel.cohorts.review.assignSuccess'),
    );

    onDialogOK(assignments.length);
  } catch (err) {
    console.error('Error assigning clients:', err);
    notifyNegative(i18n.t('common.errors.generic'));
  }
}

function onNext() {
  const shouldAssignImmediately = goToNextStep();

  if (shouldAssignImmediately) {
    onAssign();
  }
}

function onBack() {
  goToPreviousStep();
}

function onCancel() {
  trackDialogCancelled();
  onDialogCancel();
}

initializeReview({
  clients: clientsStore.regularClients,
  columns: columns.value,
});
</script>

<template>
  <q-dialog
    ref="dialogRef"
    :maximized="$q.platform.is.mobile"
    persistent
    @hide="onDialogHide"
  >
    <q-card class="review-assign-dialog">
      <q-card-section class="row items-center">
        <div class="text-h6 ellipsis col">
          {{ i18n.t('settings.careModel.cohorts.review.reviewTitle') }}
        </div>

        <div class="col-auto">
          <q-btn
            flat
            round
            dense
            icon="close"
            @click="onCancel"
          />
        </div>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div
          v-if="step === STEPS.SUGGESTED"
          class="text-body2"
        >
          {{ i18n.t('settings.careModel.cohorts.review.suggestedDescription') }}
        </div>

        <banner
          v-else
          type="info"
          :message="i18n.t(
            'settings.careModel.cohorts.review.manualDescription',
          )"
        />
      </q-card-section>

      <q-card-section>
        <review-suggested-assignments-table
          v-if="step === STEPS.SUGGESTED && hasSuggestedAssignments"
          :rows="suggestedReviewRows"
          @toggle-all="onToggleAll"
          @toggle-row="onToggleSuggestedRow"
        />

        <review-manual-assignments-table
          v-else-if="step === STEPS.MANUAL && hasManualAssignments"
          :rows="manualAssignments"
          :cohort-options="cohortOptions"
          @update-selection="updateManualSelection"
        />

        <div
          v-else
          class="text-grey-7"
        >
          {{ i18n.t('settings.careModel.cohorts.review.noReviewItems') }}
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          v-if="canGoBack"
          flat
          :label="i18n.t('common.buttonDirectionBack')"
          color="primary"
          @click="onBack"
        />

        <q-space />

        <q-btn
          flat
          :label="i18n.t('common.buttonCancel')"
          color="negative"
          @click="onCancel"
        />

        <q-btn
          v-if="step === STEPS.SUGGESTED"
          flat
          :label="suggestedStepButtonLabel"
          color="primary"
          :disable="!canGoNext || isProcessing"
          :loading="isProcessing"
          @click="onNext"
        />

        <q-btn
          v-else
          flat
          :label="i18n.t('settings.careModel.cohorts.review.assignButton')"
          color="primary"
          :disable="!canAssign || isProcessing"
          :loading="isProcessing"
          @click="onAssign"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style scoped>
.review-assign-dialog {
  min-width: 80vw;
}
</style>
